<div class="duty-roster-container" [style.--col-width.px]="columnWidthPx" [class.roster-compact]="columnWidthPx < 50">

  <div class="sticky roster-toolbar">
    <h2 class="text-base font-bold">Duty Roster</h2>
    <div class="toolbar-row toolbar-row-top">
      <form class="w-64" [formGroup]="dateForm">
        <tui-input-date-range tuiTextfieldSize="s" formControlName="testValue"
          tuiUnfinishedValidator="Finish filling the field">
          Choose dates
          <input placeholder="From - To" tuiTextfieldLegacy />
        </tui-input-date-range>
      </form>

      <div class="w-44">
        <tui-combo-box tuiTextfieldSize="s" [stringify]="stringifySortOption" [(ngModel)]="sortBy"
          (ngModelChange)="onSortChange($event)">
          Sort by
          <input tuiTextfieldLegacy placeholder="Select sort" [value]="stringifySortOption(sortBy)" />
          <tui-data-list-wrapper *tuiDataList [items]="sortOptions"
            [itemContent]="stringifySortOption | tuiStringifyContent" />
        </tui-combo-box>
      </div>
      <div class="w-48">
        <tui-combo-box tuiTextfieldSize="s" [stringify]="stringifyNurses.bind(this)" [(ngModel)]="selectedFilterNurse">
          Filter by Nurse
          <input tuiTextfieldLegacy placeholder="Select Nurse" [value]="stringifyNurses(selectedFilterNurse)" />
          <tui-data-list-wrapper *tuiDataList [items]="nurses | tuiFilterByInput"
            [itemContent]="stringifyNurses | tuiStringifyContent" />
        </tui-combo-box>
      </div>

      <button (click)="fetchDutyRoster()" appearance="secondary" tuiButton type="button" size="s"
        style="border: 2px solid #e5e7ea; font-weight: 800">
        Search
      </button>
      <button (click)="resetSearchAndFilters()" appearance="flat" iconStart="@tui.rotate-cw" tuiButton type="button"
        size="s">
        Clear
      </button>

      <div class="ml-auto">
        <button type="button" appearance="secondary" tuiButton size="s" (click)="openScheduleDialog()"
          style="border: 2px solid #e5e7ea; font-weight: 800">
          Schedule a Shift
        </button>
      </div>
    </div>

    <div class="flex items-center justify-end gap-8 mt-6">
      <label class="toggle-switch" [appDisableUnlessPermission]="'SHIFT_UPDATE'">
        <input type="checkbox" [checked]="includeArchived" (change)="toggleIncludeArchived(!includeArchived)" />
        <span class="toggle-track"><span class="toggle-thumb"></span></span>
        <span class="toggle-label">Show Archived</span>
      </label>

      <div class="col-width-slider flex items-center gap-2">
        <span class="col-width-label text-xs text-gray-400">20px</span>
        <input type="range" min="20" max="200" step="10" [(ngModel)]="columnWidthPx"
          (ngModelChange)="onColumnWidthChange($event)" [attr.aria-label]="'Column width'" title="Column width"
          class="cursor-pointer" />
        <span class="col-width-label text-xs text-gray-400">200px</span>
      </div>
    </div>

    <p *ngIf="includeArchived" class="toolbar-hint">
      Archived shifts stay on the roster for reference; restore (shift update access) before editing.
    </p>
  </div>

  <div *ngIf="loading" class="loader"></div>

  <div class="table-container mt-2" *ngIf="dutyRosterData.length > 0 && !loading">
    <table class="duty-roster-table duty-roster-table-compact">
      <thead>
        <tr>
          <th class="fixed-left-seq text-center">#</th>
          <th class="fixed-left client-col-header">
            <div class="flex items-center justify-between">
              <span>Client Details</span>
              <button type="button" class="expand-all-btn" (click)="toggleExpandAll()">
                {{ allExpanded ? 'Collapse All' : 'Expand All' }}
              </button>
            </div>
          </th>
          <!-- Dynamically generate table headers based on the date range -->
          <th *ngFor="let date of dateRange" class="date-column text-center"
            [attr.title]="date | date: 'EEEE, MMMM d, yyyy'">
            <span class="day-number font-bold text-xs">{{ date | date: "d" }}</span>
            <span class="day-weekday text-[9px] block font-semibold uppercase"
              [class.text-[#ef4444]]="date.getDay() === 0 || date.getDay() === 6"
              [class.text-gray-400]="date.getDay() !== 0 && date.getDay() !== 6">
              {{ date | date: (columnWidthPx >= 100 ? "EEEE" : columnWidthPx >= 60 ? "EEE" : "EEEEE") }}
            </span>
            <span class="day-month text-[9px] block text-gray-500 font-normal">
              {{ date | date: (columnWidthPx >= 100 ? "MMMM" : "MMM") }}
            </span>
          </th>
          <!-- <th class="fixed-right">Total Charges</th> -->
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let client of getFilteredAndSortedDutyRosterData(); let i = index">
          <!-- Row sequence number -->
          <td class="fixed-left-seq text-center font-semibold text-xs text-[#191c30b3]">
            {{ clientSequenceNumbers[client._id] || "—" }}
          </td>
          <!-- Client Details: collapsed vs expanded -->
          <td class="fixed-left client-cell">
            <div class="client-summary-row" (click)="toggleExpand(client)">
              <button type="button" class="chevron-btn" [class.expanded]="isExpanded(client)"
                [attr.aria-label]="isExpanded(client) ? 'Collapse client' : 'Expand client'">
                <svg width="12" height="12" viewBox="0 0 12 12">
                  <path d="M2 4 L6 8 L10 4" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </button>
              <div class="client-id-line">
                <div *ngIf="client.regType === 'Registered' || client.regType === 'Regular Client'" class="reg-dot"></div>
                <span class="client-id-text">{{ formatClientId(client) }}</span>
              </div>
            </div>

            <!-- Client/Patient Name - Always visible, styled nicely, clicking it opens the edit dialog -->
            <div class="client-name-line" (click)="showClientDialog(client._id)">
              {{ client.guardianName || "—" }}
            </div>

            <!-- Total Charge - Always visible under the name, styled as a premium compact badge/text -->
            <div class="client-charge-line" (click)="showClientDialog(client._id)">
              Total Charge: Rs. {{ client.totalCharge | number: "1.0-0" }}
            </div>

            <!-- Expanded detail card -->
            <div class="client-expanded-detail" *ngIf="isExpanded(client)">
              <div class="detail-line"><span class="detail-label">Client :</span> {{ client.guardianName || "—" }}</div>
              <div class="detail-line"><span class="detail-label">Patient :</span> {{ client.clientName || "—" }}</div>
              <div class="detail-line"><span class="detail-label">Address :</span> {{ client.address }}</div>

              <ng-container *ngIf="getFirstShift(client) as firstShift">
                <ng-container *ngIf="client.regType === 'Inward'">
                  <div class="detail-line"><span class="detail-label">Room :</span> {{ firstShift.room }}</div>
                  <div class="detail-line"><span class="detail-label">BHT Number :</span> {{ firstShift.bhtNumber }}
                  </div>
                </ng-container>
                <div class="detail-line"><span class="detail-label">Meal Opt :</span> {{ firstShift.mealOption }}</div>
                <div class="detail-line"><span class="detail-label">Shift Charges :</span> Rs. {{ firstShift.charges |
                  number: "1.0-0" }}</div>
                <div class="detail-line"><span class="detail-label">Shift Type :</span> {{ firstShift.shiftType }}</div>
              </ng-container>

              <div class="detail-line"><span class="detail-label">Contact :</span> {{ client.contactNumber }}</div>
            </div>
          </td>

          <!-- Shift Details for Each Date -->
          <td *ngFor="let date of dateRange" class="date-cell"
            [class.off-day-cell]="hasOffDayForDate(client.shifts, date)" [attr.data-client-id]="client._id"
            [attr.data-date]="date | date: 'yyyy-MM-dd'">
            <div *ngFor="let shift of getShiftsForDate(client.shifts, date)">
              <!-- Off Day Display -->
              <div *ngIf="shift.isOffDay" class="off-day-card max-w-60" title="Off day">
                <div class="off-day-header">Off Day</div>
                <span class="compact-letter hidden">-</span>
              </div>

              <!-- Regular Shift Display (unchanged styling) -->
              <div *ngIf="!shift.isOffDay" class="shift-card max-w-60"
                [class.shift-last-added-updated]="isLastAddedOrUpdated(client, shift)"
                [class.opacity-60]="shift.archived" [class.cursor-pointer]="!shift.archived && loggedInUserCanEditShift"
                [class.cursor-default]="shift.archived || !loggedInUserCanEditShift"
                [attr.title]="getShiftTooltip(client, shift)" (click)="onRosterShiftCardClick(shift)">
                <span class="compact-letter hidden">{{ (shift.nurseName || 'U').charAt(0) }}</span>
                <div *ngIf="shift.archived"
                  class="text-[10px] font-semibold text-[#92400e] bg-[#fde68a] inline-block px-2 py-[1px] rounded-full mb-1">
                  Archived
                </div>
                <div class="shift-header">{{ shift.nurseName }}</div>
                <div class="shift-details">
                  <div class="shift-nurse">Roster: {{ shift.roster }}</div>
                  <div class="shift-nurse">
                    {{ shift.roosterType }}
                  </div>
                </div>
                <div *ngIf="shift.name || shift.mobile || shift.room || shift.bhtNumber">
                  <div *ngIf="shift.name" class="shift-nurse">Patient: {{ shift.name }}</div>
                  <div *ngIf="shift.mobile" class="shift-nurse">Mobile: {{ shift.mobile }}</div>
                  <div *ngIf="shift.room || shift.bhtNumber" class="shift-nurse">
                    Room/Bed: {{ shift.room }}/{{ shift.bhtNumber }}
                  </div>
                </div>
                <button *ngIf="shift.archived" type="button"
                  class="text-[10px] mt-1 text-[#526ED3] underline decoration-dotted underline-offset-2 cursor-pointer bg-transparent border-none p-0"
                  (click)="$event.stopPropagation(); restoreShift(shift._id)"
                  [appDisableUnlessPermission]="'SHIFT_UPDATE'">
                  Restore
                </button>
              </div>
            </div>
          </td>

          <!-- Total Charges -->
          <!-- <td class="fixed-right">Rs. {{ client.totalCharge | number: "1.0-0" }}</td> -->
        </tr>
      </tbody>
    </table>
  </div>

  <!-- No Data Message -->
  <div *ngIf="!loading && dutyRosterData.length === 0" class="text-center text-gray-500">
    No data available for the selected date range.
  </div>
</div>

<!-- ==================================================================== -->
<!-- Dialog templates below are UNCHANGED from the original component.     -->
<!-- Only the roster/table markup above was redesigned.                    -->
<!-- ==================================================================== -->

<!-- Single Update Client Dialog Template -->
<ng-template let-observer [tuiDialogOptions]="{ label: 'Update Client', size: 's' }" [(tuiDialog)]="openClientBox"
  (tuiDialogClose)="onDialogClose()">
  <form class="mt-3" [formGroup]="clientForm" (ngSubmit)="updateClient()">
    <div *ngIf="popupLoading" class="loader"></div>

    <label tuiLabel>
      <tui-input-date formControlName="registrationDate">Registration Date <input tuiTextfieldLegacy /></tui-input-date>
      <tui-error formControlName="registrationDate" class="mb-3" [error]="[] | tuiFieldError | async" />
    </label>

    <label tuiLabel>
      <tui-input formControlName="guardianNIC">NIC Number <input tuiTextfieldLegacy type="text" /></tui-input>
      <tui-error formControlName="guardianNIC" class="mb-3" [error]="[] | tuiFieldError | async" />
    </label>

    <div class="w-full">
      <label tuiLabel>
        <tui-textarea formControlName="guardianAddress" [tuiTextfieldLabelOutside]="true" placeholder="Enter Address"
          title="Textarea Input">Address</tui-textarea>
        <tui-error formControlName="guardianAddress" class="mb-3" [error]="[] | tuiFieldError | async" />
      </label>
    </div>

    <label tuiLabel>
      <tui-input formControlName="guardianContactNo">Client Contact Number (Whatsapp)<input tuiTextfieldLegacy
          type="text" /></tui-input>
      <tui-error class="mb-3" formControlName="guardianContactNo" [error]="[] | tuiFieldError | async" />
    </label>

    <label tuiLabel>
      <tui-input formControlName="email">Client Email<input tuiTextfieldLegacy type="text" /></tui-input>
      <tui-error formControlName="email" class="mb-3" [error]="[] | tuiFieldError | async" />
    </label>

    <label tuiLabel>
      <tui-input formControlName="guardianName">Client Name <input tuiTextfieldLegacy type="text" /></tui-input>
      <tui-error formControlName="guardianName" class="mb-3" [error]="[] | tuiFieldError | async" />
    </label>

    <label tuiLabel>
      <tui-input formControlName="name" class="">Patient Name<input tuiTextfieldLegacy type="text" /></tui-input>
      <tui-error formControlName="name" class="mb-3" [error]="[] | tuiFieldError | async" />
    </label>

    <label tuiLabel>
      <tui-input-date formControlName="dateOfBirth">Patient Date of birth<input tuiTextfieldLegacy /></tui-input-date>
      <tui-error formControlName="dateOfBirth" class="mb-3" [error]="[] | tuiFieldError | async" />
    </label>

    <label tuiLabel>
      <tui-input formControlName="age">Patient Age <input tuiTextfieldLegacy type="text" /></tui-input>
      <tui-error formControlName="age" class="mb-3" [error]="[] | tuiFieldError | async" />
    </label>

    <tui-combo-box formControlName="registrationType" [tuiTextfieldLabelOutside]="true"
      placeholder="Select Registration Type">
      <tui-data-list-wrapper *tuiDataList [items]="registrationOptions | tuiFilterByInput" />
      Registration Type
    </tui-combo-box>
    <tui-error formControlName="registrationType" class="mb-3" [error]="[] | tuiFieldError | async" />

    <p *ngIf="clientForm.get('registrationType')?.value && displayedRegistrationFee != null"
      class="text-sm text-[#191C30E5] mb-3">
      Registration Fee (LKR):
      <span class="font-semibold">{{ displayedRegistrationFee | number: "1.0-0" }}</span>
    </p>

    <div *ngIf="clientForm.get('registrationType')?.value === 'One Year'">
      <label tuiLabel>
        <tui-input-date formControlName="expiryDate">Expiry Date <input tuiTextfieldLegacy /></tui-input-date>
        <tui-error formControlName="expiryDate" class="mb-3" [error]="[] | tuiFieldError | async" />
      </label>
    </div>

    <label tuiLabel class="mt-2">
      <tui-textarea formControlName="currentCondition" [tuiTextfieldLabelOutside]="true"
        placeholder="Enter Current Condition" title="Textarea Input">Current Condition</tui-textarea>
      <tui-error formControlName="currentCondition" [error]="[] | tuiFieldError | async" />
    </label>

    <div class="flex justify-end mt-4">
      <button tuiButton type="submit" [appDisableUnlessPermission]="'CLIENT_UPDATE'">Update Client</button>
    </div>
  </form>
</ng-template>

<!-- Single Update Shift Dialog Template -->
<ng-template let-observer [tuiDialogOptions]="{ label: 'Update Shift', size: 'm' }" [(tuiDialog)]="open"
  (tuiDialogClose)="onDialogClose()">
  <form class="mt-3" [formGroup]="shiftForm" (ngSubmit)="updateShift()">
    <div *ngIf="popupLoading" class="loader"></div>
    <p class="text-xs text-[#6b7280] mb-3">
      Only active (non-archived) shifts can be updated or deleted from this form.
    </p>

    <tui-combo-box [stringify]="stringifyClientType" formControlName="clientType">
      Select Client Type
      <input tuiTextfieldLegacy placeholder="Select Client Type" />
      <tui-data-list-wrapper *tuiDataList [items]="clientTypeOptions"
        [itemContent]="stringifyClientType | tuiStringifyContent" />
    </tui-combo-box>
    <p class="text-xs text-[#8a8a8a] mt-1 mb-3 flex items-center gap-1">
      <span
        class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#e5e7eb] text-[#4b5563] text-[10px] font-bold">i</span>
      Client type cannot be changed for an existing shift.
    </p>
    <tui-error formControlName="clientType" class="mb-3" [error]="[] | tuiFieldError | async" />

    <tui-combo-box formControlName="client" [tuiTextfieldLabelOutside]="true" [stringify]="stringifyClients"
      placeholder="Select Client">Select Client
      <tui-data-list-wrapper *tuiDataList [items]="clients | tuiFilterByInput"
        [itemContent]="stringifyClients | tuiStringifyContent">
      </tui-data-list-wrapper>
    </tui-combo-box>
    <label class="flex items-center gap-2 text-xs text-[#191C30E5] select-none mt-1">
      <input type="checkbox" [checked]="includeInactiveClients"
        (change)="toggleIncludeInactiveClients(!includeInactiveClients)" />
      Include inactive / archived clients
    </label>
    <p *ngIf="isSelectedShiftClientInactive()" class="text-xs text-[#8a6d1a] mt-1 flex items-center gap-1">
      <span
        class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#fde68a] text-[#92400e] text-[10px] font-bold">!</span>
      This client is archived or inactive; saving will use historical data only.
    </p>
    <tui-error class="mb-3" [error]="[] | tuiFieldError | async" />

    <tui-combo-box formControlName="nurse" [tuiTextfieldLabelOutside]="true" [stringify]="stringifyNurses"
      placeholder="Select Nurse">Select Nurse
      <tui-data-list-wrapper *tuiDataList [items]="nurses | tuiFilterByInput"
        [itemContent]="stringifyNurses | tuiStringifyContent">
      </tui-data-list-wrapper>
    </tui-combo-box>
    <tui-error class="mb-3" formControlName="nurse" [error]="[] | tuiFieldError | async" />

    <ng-container *ngIf="!isInwardClient">
      <tui-combo-box [stringify]="stringifyDropdown" formControlName="typeOption" class="mb-3">
        Select Type
        <input tuiTextfieldLegacy placeholder="Select Type" />
        <tui-data-list-wrapper *tuiDataList [items]="typeOptions"
          [itemContent]="stringifyDropdown | tuiStringifyContent" />
      </tui-combo-box>
      <tui-error formControlName="typeOption" class="mb-3" [error]="[] | tuiFieldError | async" />
    </ng-container>

    <tui-combo-box [stringify]="stringifyDropdown" formControlName="roster">
      Select Roster
      <input tuiTextfieldLegacy placeholder="Select Roster" />
      <tui-data-list-wrapper *tuiDataList [items]="rosterItems"
        [itemContent]="stringifyDropdown | tuiStringifyContent" />
    </tui-combo-box>
    <tui-error formControlName="roster" class="mb-3" [error]="[] | tuiFieldError | async" />

    <div *ngIf="shiftForm.get('roster')?.value?.value === 12">
      <tui-combo-box [stringify]="stringifyDropdown" formControlName="rosterType">
        Select Roster Type
        <input tuiTextfieldLegacy placeholder="Select Roster type" />
        <tui-data-list-wrapper *tuiDataList [items]="rosterType"
          [itemContent]="stringifyDropdown | tuiStringifyContent" />
      </tui-combo-box>
      <tui-error formControlName="rosterType" class="mb-3" [error]="[] | tuiFieldError | async" />
    </div>

    <tui-combo-box [stringify]="stringifyDropdown" formControlName="mealOption">
      Select Meal Option
      <input tuiTextfieldLegacy placeholder="Select Roster" />
      <tui-data-list-wrapper *tuiDataList [items]="mealOptions"
        [itemContent]="stringifyDropdown | tuiStringifyContent" />
    </tui-combo-box>
    <tui-error formControlName="mealOption" class="mb-3" [error]="[] | tuiFieldError | async" />

    <ng-container *ngIf="!isInwardClient">
      <div class="w-full">
        <label tuiLabel>
          <tui-textarea formControlName="address" [tuiTextfieldLabelOutside]="true" placeholder="Enter Address"
            title="Textarea Input">
            Address
          </tui-textarea>
          <tui-error formControlName="address" class="mb-3" [error]="[] | tuiFieldError | async" />
        </label>
      </div>
    </ng-container>

    <tui-input formControlName="charge">Charge <input tuiTextfieldLegacy type="text" /></tui-input>
    <tui-error formControlName="charge" class="mb-3" [error]="[] | tuiFieldError | async" />

    <tui-input formControlName="salary">salary <input tuiTextfieldLegacy type="text" /></tui-input>
    <tui-error formControlName="salary" class="mb-3" [error]="[] | tuiFieldError | async" />

    <!-- Room / BHT Number are Inward-specific operational identifiers
         and intentionally never shown for Registered or Non Registered
         shifts, even when the typeOption is "NHP (National Hospital)". -->
    <div *ngIf="isInwardClient">
      <div class="w-full">
        <label tuiLabel>
          <tui-input formControlName="room">
            Room
            <input tuiTextfieldLegacy type="text" />
          </tui-input>
          <tui-error formControlName="room" class="mb-3" [error]="[] | tuiFieldError | async"></tui-error>
        </label>
      </div>

      <div class="w-full">
        <label tuiLabel>
          <tui-input formControlName="bhtNumber">
            BHT Number
            <input tuiTextfieldLegacy type="text" />
          </tui-input>
          <tui-error formControlName="bhtNumber" class="mb-3" [error]="[] | tuiFieldError | async"></tui-error>
        </label>
      </div>
    </div>

    <div>
      <tui-input-date-range formControlName="startDateRange" [tuiTextfieldLabelOutside]="true"
        placeholder="Select Date Range">
        Select Date Range
      </tui-input-date-range>
      <tui-error formControlName="startDateRange" class="mb-3" [error]="[] | tuiFieldError | async"></tui-error>
    </div>

    <div class="w-full" *ngIf="!isInwardClient">
      <label tuiLabel>
        <tui-textarea formControlName="notes" [tuiTextfieldLabelOutside]="true" placeholder="Enter notes"
          title="Textarea Input">
          Additional Notes
        </tui-textarea>
        <tui-error formControlName="notes" class="mb-3" [error]="[] | tuiFieldError | async" />
      </label>
    </div>

    <!-- In-Form Shift Error Alert Box -->
    <div *ngIf="shiftErrorMessage"
      class="mt-3 mb-4 p-3.5 rounded-lg bg-[#fef2f2] border border-[#fecaca] text-[#991b1b] text-xs flex items-start gap-2.5 shadow-sm">
      <div class="flex-1 font-semibold leading-relaxed">{{ shiftErrorMessage }}</div>
    </div>

    <div class="flex justify-between items-center">
      <button tuiButton type="button" appearance="danger" (click)="deleteShift()"
        [appDisableUnlessPermission]="'SHIFT_DELETE'">
        Delete shift
      </button>
      <button tuiButton type="submit" [appDisableUnlessPermission]="'SHIFT_UPDATE'">Update shift</button>
    </div>
  </form>
</ng-template>

<!-- Delete shift confirmation (stacked over Update Shift) -->
<ng-template [tuiDialogOptions]="{ label: 'Delete shift', size: 's' }" [(tuiDialog)]="confirmDeleteDialogOpen">
  <p class="text-sm mb-4">
    Are you sure you want to delete this shift? It will be archived and removed from the active roster.
  </p>
  <div class="flex justify-end gap-2 flex-wrap">
    <button tuiButton type="button" appearance="secondary" (click)="onConfirmDeleteCancel()">
      Cancel
    </button>
    <button tuiButton type="button" appearance="primary" (click)="onConfirmDeleteYes()">
      Yes
    </button>
  </div>
</ng-template>

<!-- Schedule Shift Dialog (embedded CheckinComponent) -->
<ng-template let-observer [tuiDialogOptions]="{ size: 'm' }" [(tuiDialog)]="scheduleDialogOpen">
  <app-checkin [inDialog]="true" (shiftCreated)="onShiftCreatedFromDialog($event)"
    (dialogCancel)="scheduleDialogOpen = false"></app-checkin>
</ng-template>